import CloseIcon from '@mui/icons-material/Close';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import Loader from 'components/Loader';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { ManifestErrors } from './ManifestFeedback';
import { CaseOrganizationExecutionMode, caseOrganizationHandlingLabels } from './caseOrganizationPlan';
import { ManifestDestination } from './loadManifestDestination';
import { ManifestWorkbookContextResult } from './manifestWorkbookContext';
import { useManifestTemplateDownload } from './useManifestTemplateDownload';

const handlingGuidance: Record<CaseOrganizationExecutionMode, string> = {
  routine: 'Additional slides in existing cases are retained, subject to moves. Other cases remain.',
  selectedCaseOverride: 'Each case in the plan keeps only its planned slides. Other cases remain, subject to moves.',
  wholeStudyReplacement:
    'Each listed case keeps only its planned slides. Existing case labels absent from the plan are deleted on apply.',
};

const replacementWarning =
  'Uploading replaces the entire unsaved case plan. Omitted rows are removed from the plan. Saved study assignments change only after Review changes and Apply.';

interface CaseManifestFileMenuProps {
  canDownload: boolean;
  canUpload: boolean;
  caseCount: number;
  executionMode: CaseOrganizationExecutionMode | null;
  exporting: boolean;
  loading: boolean;
  onDownloadPlan: () => void;
  onUploadClick: () => void;
  closeMenuRef?: React.MutableRefObject<(() => void) | null>;
  resolving: boolean;
  slideCount: number;
}

export const CaseManifestFileMenu: FunctionComponent<CaseManifestFileMenuProps> = ({
  canDownload,
  canUpload,
  caseCount,
  executionMode,
  exporting,
  loading,
  closeMenuRef,
  onDownloadPlan,
  onUploadClick,
  resolving,
  slideCount,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const downloadTemplate = useManifestTemplateDownload();
  const closeMenu = () => setAnchorEl(null);
  if (closeMenuRef) closeMenuRef.current = closeMenu;
  const downloadDisabled = !canDownload || loading || exporting;

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<DescriptionOutlinedIcon />}
        aria-haspopup="menu"
        aria-expanded={anchorEl ? 'true' : undefined}
        aria-controls={anchorEl ? 'slide-manifest-file-menu' : undefined}
        aria-busy={resolving || exporting}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ mr: 1 }}
      >
        Slide manifest file
      </Button>
      <Menu
        id="slide-manifest-file-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        MenuListProps={{ 'aria-label': 'Slide manifest file' }}
      >
        <ListSubheader
          component="div"
          sx={{ whiteSpace: 'normal', lineHeight: 1.4, py: 1, bgcolor: 'transparent', maxWidth: 360 }}
        >
          <Typography variant="caption" color="text.secondary" component="p">
            {slideCount} slides in {caseCount} cases in the current plan.
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {replacementWarning}
            {executionMode ? ` ${handlingGuidance[executionMode]}` : ''}
          </Typography>
          {loading && (
            <Typography variant="caption" color="text.secondary" component="p">
              Wait for all study and slide information to load before downloading the plan.
            </Typography>
          )}
        </ListSubheader>
        <MenuItem
          disabled={downloadDisabled}
          onClick={() => {
            closeMenu();
            onDownloadPlan();
          }}
        >
          Download current plan
        </MenuItem>
        <MenuItem
          disabled={downloadTemplate.isLoading}
          onClick={() => {
            closeMenu();
            downloadTemplate.mutate();
          }}
        >
          Download template
        </MenuItem>
        <MenuItem
          disabled={!canUpload || resolving || exporting}
          onClick={() => {
            closeMenu();
            onUploadClick();
          }}
        >
          Upload Excel file
        </MenuItem>
      </Menu>
    </>
  );
};

interface CaseManifestFileStatusProps {
  canFixInCasePlan: boolean;
  canMatchContext: boolean;
  contextFeedback?: Extract<ManifestWorkbookContextResult, { status: 'valid' }>;
  destination?: ManifestDestination;
  destinationError?: string;
  disabled: boolean;
  downloadError: string | null;
  errors: string[];
  executionMode: CaseOrganizationExecutionMode | null;
  exporting: boolean;
  onConfirmOverwrite: () => void;
  onDismiss: () => void;
  onDismissDownloadError: () => void;
  onFixInCasePlan: () => void;
  onKeepContext: () => void;
  onMatchContext: () => void;
  onOverwriteAccepted: (accepted: boolean) => void;
  onRetryDestination?: () => void;
  overwriteAccepted: boolean;
  pendingOverwrite: boolean;
  resolving: boolean;
}

export const CaseManifestFileStatus: FunctionComponent<CaseManifestFileStatusProps> = ({
  canFixInCasePlan,
  canMatchContext,
  contextFeedback,
  destination,
  destinationError,
  disabled,
  downloadError,
  errors,
  executionMode,
  exporting,
  onConfirmOverwrite,
  onDismiss,
  onDismissDownloadError,
  onFixInCasePlan,
  onKeepContext,
  onMatchContext,
  onOverwriteAccepted,
  onRetryDestination,
  overwriteAccepted,
  pendingOverwrite,
  resolving,
}) => {
  const hasPendingDecision = pendingOverwrite || Boolean(contextFeedback);
  useEffect(() => {
    if (!hasPendingDecision) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onDismiss();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [hasPendingDecision, onDismiss]);

  if (!downloadError && !exporting && !pendingOverwrite && !contextFeedback && errors.length === 0 && !resolving) {
    return null;
  }

  return (
    <Box data-testid="case-manifest-file-status" sx={{ width: '100%', mb: 1.5 }}>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={onDismissDownloadError} closeText="Dismiss download error">
          {downloadError}
        </Alert>
      )}
      {exporting && (
        <Box sx={{ mb: 1 }}>
          <Loader inline message="Preparing the case plan download…" ariaLabel="Preparing case plan download" />
        </Box>
      )}
      {pendingOverwrite && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2">
            The case plan changed after download. Uploading this file will replace those newer edits.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox checked={overwriteAccepted} onChange={(_event, checked) => onOverwriteAccepted(checked)} />
            }
            label="Replace newer table edits with the uploaded file"
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button color="inherit" onClick={onDismiss} disabled={resolving}>
              Keep current plan
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onConfirmOverwrite}
              disabled={disabled || resolving || !overwriteAccepted}
            >
              Replace current plan
            </Button>
          </Box>
        </Alert>
      )}
      {contextFeedback && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {!canMatchContext && (
            <Typography variant="body2">The case manifest file refers to a different study or lab.</Typography>
          )}
          <Typography variant="body2">
            Current handling: {executionMode ? caseOrganizationHandlingLabels[executionMode] : 'Choose an operation'}
          </Typography>
          <Typography variant="body2">
            Case manifest file handling: {caseOrganizationHandlingLabels[contextFeedback.context.handling]}
          </Typography>
          {destination && (
            <Typography variant="body2">
              Destination: {destination.studyName} ({destination.labName})
            </Typography>
          )}
          {destinationError && <Typography variant="body2">{destinationError}</Typography>}
          <Button color="inherit" onClick={onKeepContext} disabled={disabled || resolving}>
            Keep current context
          </Button>
          {canMatchContext && (
            <Button color="inherit" onClick={onMatchContext} disabled={disabled || resolving}>
              Match case manifest file context
            </Button>
          )}
          {destinationError && onRetryDestination && (
            <Button color="inherit" onClick={onRetryDestination} disabled={disabled || resolving}>
              Retry destination lookup
            </Button>
          )}
        </Alert>
      )}
      {errors.length > 0 && (
        <Box role="alert" sx={{ mb: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <ManifestErrors messages={errors} />
            {canFixInCasePlan && (
              <Button color="inherit" onClick={onFixInCasePlan} disabled={disabled || resolving}>
                Fix in case plan
              </Button>
            )}
          </Box>
          <Tooltip title="Dismiss import error">
            <IconButton color="inherit" size="small" aria-label="Dismiss import error" onClick={onDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      {resolving && (
        <Box aria-live="polite">
          <Box sx={{ mb: 1 }}>
            <Loader inline message="Validating manifest..." />
          </Box>
          <Button color="inherit" onClick={onDismiss}>
            Keep current plan
          </Button>
        </Box>
      )}
    </Box>
  );
};
